import { useEffect, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Gavel, Plus } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterPanel } from "@/components/admin/FilterPanel";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useFilters } from "@/lib/useFilters";
import { useSelection } from "@/lib/useSelection";

const DISC_FILTERS = [
  { key: "search", type: "search", label: "Search complaint", paths: ["complaint_summary"] },
  { key: "severity", type: "select", label: "Severity", path: "severity",
    options: [{ value: "minor", label: "Minor" }, { value: "moderate", label: "Moderate" }, { value: "severe", label: "Severe" }] },
  { key: "status", type: "select", label: "Status", path: "status",
    options: [
      { value: "open", label: "Open" }, { value: "under_investigation", label: "Under investigation" },
      { value: "resolved", label: "Resolved" }, { value: "dismissed", label: "Dismissed" },
    ] },
  { key: "date_from", type: "dateFrom", label: "Opened from", path: "created_at" },
  { key: "date_to", type: "dateTo", label: "Opened to", path: "created_at" },
];

export default function Disciplinary() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);

  const reload = async () => {
    const [r, u] = await Promise.all([api.get("/disciplinary-records"), api.get("/admin/users")]);
    setRecords(r.data);
    setUsers(u.data);
  };
  useEffect(() => { reload(); }, []);

  const filters = useFilters(records, DISC_FILTERS, { defaultSort: { field: "created_at", dir: "desc" } });
  const selection = useSelection(filters.filtered);
  const isAdmin = user.role === "admin";

  return (
    <div className="animate-fade-up" data-testid="disciplinary-page">
      <div className="mb-6 flex flex-wrap justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">IBTHM · Ethics &amp; conduct</div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3"><Gavel className="h-7 w-7" />Disciplinary records</h1>
          <p className="text-sm text-slate-500 mt-1">Open and adjudicate complaints. Public summaries appear in the registry.</p>
        </div>
        <NewDisciplinaryDialog users={users.filter((u) => u.role === "practitioner")} onCreated={reload} />
      </div>

      <FilterPanel
        schemas={DISC_FILTERS} values={filters.values} set={filters.set} clear={filters.clear}
        count={filters.count} total={records.length} activeCount={filters.activeCount}
        sortFields={[
          { key: "created_at", label: "Opened date" },
          { key: "severity", label: "Severity" },
          { key: "status", label: "Status" },
        ]}
        sort={filters.sort} setSort={filters.setSort} testid="disc-filters"
      />

      {isAdmin && <BulkActionsBar collection="disciplinary_records" selection={selection} onDone={reload} />}

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                {isAdmin && <th className="p-4 w-10"><Checkbox checked={selection.allSelected} onCheckedChange={selection.toggleAll} data-testid="disc-select-all" /></th>}
                <th className="p-4">Practitioner</th>
                <th className="p-4">Severity</th>
                <th className="p-4">Complaint</th>
                <th className="p-4">Status</th>
                <th className="p-4">Opened</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filters.filtered.length === 0 && <tr><td colSpan={isAdmin ? 7 : 6} className="p-10 text-center text-slate-500">No disciplinary records match the current filters</td></tr>}
              {filters.filtered.map((d) => {
                const u = users.find((x) => x.id === d.practitioner_user_id);
                return (
                  <tr key={d.id} className="hover:bg-slate-50">
                    {isAdmin && <td className="p-4"><Checkbox checked={selection.isSelected(d.id)} onCheckedChange={() => selection.toggle(d.id)} data-testid={`disc-select-${d.id}`} /></td>}
                    <td className="p-4 font-medium text-primary">{u?.name || d.practitioner_user_id.slice(0, 8)}</td>
                    <td className="p-4"><Badge variant="outline" className="capitalize">{d.severity}</Badge></td>
                    <td className="p-4 text-xs text-slate-700 max-w-xs truncate">{d.complaint_summary}</td>
                    <td className="p-4"><Badge variant="outline" className="capitalize">{d.status.replace(/_/g, " ")}</Badge></td>
                    <td className="p-4 text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-right">
                      <UpdateDisciplinaryDialog rec={d} onChanged={reload} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function NewDisciplinaryDialog({ users, onCreated }) {
  const [open, setOpen] = useState(false);
  const [pid, setPid] = useState("");
  const [severity, setSeverity] = useState("moderate");
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!pid || !summary) { toast.error("Select practitioner and provide summary"); return; }
    setBusy(true);
    try {
      await api.post("/disciplinary-records", { practitioner_user_id: pid, complaint_summary: summary, severity });
      toast.success("Disciplinary record opened");
      setOpen(false); setPid(""); setSummary("");
      onCreated();
    } catch (e) { toast.error(errMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-primary text-white hover:bg-[#113A64]" data-testid="disc-new-btn"><Plus className="h-4 w-4 mr-2" />Open new case</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Open disciplinary case</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="mb-1.5 block text-xs">Practitioner</Label>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="disc-practitioner"><SelectValue placeholder="Select practitioner" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Severity</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger data-testid="disc-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="minor">Minor</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="severe">Severe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Complaint summary</Label>
            <Textarea rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="disc-summary" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-primary text-white hover:bg-[#113A64]" data-testid="disc-submit">{busy ? "Opening…" : "Open case"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpdateDisciplinaryDialog({ rec, onChanged }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(rec.status);
  const [sanction, setSanction] = useState(rec.sanction || "");
  const [resolution, setResolution] = useState(rec.resolution || "");
  const [publicSummary, setPublicSummary] = useState(rec.public_summary || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/disciplinary-records/${rec.id}`, { status, sanction, resolution, public_summary: publicSummary });
      toast.success("Case updated");
      setOpen(false); onChanged();
    } catch (e) { toast.error(errMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`disc-update-${rec.id}`}>Update</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Update case</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="mb-1.5 block text-xs">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="under_investigation">Under investigation</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Sanction</Label>
            <Input value={sanction} onChange={(e) => setSanction(e.target.value)} placeholder="e.g. 90-day suspension" />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Resolution notes (internal)</Label>
            <Textarea rows={2} value={resolution} onChange={(e) => setResolution(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Public summary (shown in registry if set)</Label>
            <Textarea rows={2} value={publicSummary} onChange={(e) => setPublicSummary(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-primary text-white hover:bg-[#113A64]">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
